import { useState } from "react";
import {
  BarChart3, Activity, MousePointer2, Monitor, Globe,
  GitMerge, Target, Sparkles, FileDown, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";

import AnalyticsOverview from "@/components/admin/analytics/AnalyticsOverview";
import AnalyticsRealtime from "@/components/admin/analytics/AnalyticsRealtime";
import AnalyticsBehaviour from "@/components/admin/analytics/AnalyticsBehaviour";
import AnalyticsDevices from "@/components/admin/analytics/AnalyticsDevices";
import AnalyticsGeo from "@/components/admin/analytics/AnalyticsGeo";
import AnalyticsFunnel from "@/components/admin/analytics/AnalyticsFunnel";
import AnalyticsGoals from "@/components/admin/analytics/AnalyticsGoals";
import AnalyticsAI from "@/components/admin/analytics/AnalyticsAI";
import ExportPanel from "@/components/admin/analytics/ExportPanel";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "realtime", label: "Real-time", icon: Activity },
  { id: "behaviour", label: "Behaviour", icon: MousePointer2 },
  { id: "devices", label: "Devices", icon: Monitor },
  { id: "geo", label: "Geography", icon: Globe },
  { id: "funnel", label: "Funnel", icon: GitMerge },
  { id: "goals", label: "Goals", icon: Target },
  { id: "ai", label: "AI Insights", icon: Sparkles },
  { id: "export", label: "Export", icon: FileDown },
];

export default function AdminAnalyticsTab() {
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("30d");

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-medium">Analytics</h2>
          <p className="text-muted-foreground text-sm mt-0.5">
            Comprehensive insights into user behaviour and conversions
          </p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="overflow-x-auto">
        <div className="flex gap-0 border-b border-border min-w-max">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`analytics-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.id === "realtime" && (
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 ml-0.5" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === "overview" && (
          <AnalyticsOverview period={period} onPeriodChange={setPeriod} />
        )}
        {activeTab === "realtime" && <AnalyticsRealtime />}
        {activeTab === "behaviour" && <AnalyticsBehaviour period={period} />}
        {activeTab === "devices" && <AnalyticsDevices period={period} />}
        {activeTab === "geo" && <AnalyticsGeo period={period} />}
        {activeTab === "funnel" && <AnalyticsFunnel period={period} />}
        {activeTab === "goals" && <AnalyticsGoals period={period} onPeriodChange={setPeriod} />}
        {activeTab === "ai" && <AnalyticsAI period={period} />}
        {activeTab === "export" && <ExportPanel />}
      </div>
    </div>
  );
}
